import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Splash } from './Splash';
import { useSplash } from '../hooks/UseSplash.tsx';

/**
 * Shows the <Splash> over the app on cold launch — but ONLY when the app first
 * paints on a login route (see SPLASH_PATHS). The splash is a login-entry brand
 * moment; deep-linking straight into another flow (e.g. /prequalify) should not
 * trigger it. Whether to show is captured once, on first mount, so a later
 * navigation can't yank the splash mid-animation.
 *
 * Regardless of route it still removes the static #boot-splash from index.html
 * on mount, so that pre-React shell never stays stuck covering a non-login
 * screen. (index.html itself hides the boot shell up-front on non-login paths.)
 *
 * Must render inside <BrowserRouter> (it reads the location). Usage:
 *
 *   <BrowserRouter>
 *     <SplashGate ready={authResolved}>
 *       <Routes> ... </Routes>
 *     </SplashGate>
 *   </BrowserRouter>
 *
 * If you don't have a readiness signal yet, pass ready={true} — the splash then
 * dismisses purely on the minimum timer.
 */
export interface SplashGateProps {
  children: ReactNode;
  /** App readiness. Splash holds until this is true (and min time elapses). */
  ready?: boolean;
  /** Optional override for the minimum display time. */
  minDurationMs?: number;
}

/** Routes whose cold launch shows the splash. */
const SPLASH_PATHS = ['/', '/login'];

export function SplashGate({ children, ready = true, minDurationMs }: SplashGateProps) {
  const { pathname } = useLocation();
  // Capture on first mount only — the splash is a cold-launch event, so a later
  // route change must not flip it on or off.
  const [enabled] = useState(() => SPLASH_PATHS.includes(pathname));

  const { visible, leaving, onExited } = useSplash({ ready, minDurationMs });

  // Tear down the pre-React boot shell once we've mounted, on every route.
  useEffect(() => {
    const boot = document.getElementById('boot-splash');
    if (boot) boot.remove();
  }, []);

  return (
    <>
      {children}
      {enabled && visible && <Splash leaving={leaving} onExited={onExited} />}
    </>
  );
}
